import {
    useMutation,
    UseMutationOptions,
    useQuery
} from '@tanstack/react-query';
import { FlowAPI } from '@newo/api/gateway';
import type {
    IFlowData,
    IFlowEmptyData,
    IFlowTemplateData,
    IFlowDuplicateData
} from '@newo/api/gateway';
import axios from '@/utils/api/axiosInstance';

export const flowAPI = new FlowAPI(axios);

type IQueryKeys = {
    getFlows: () => [string];
    getFlow: (flowId: string) => [string, string];
    getAgentFlows: (agentPersonId: string) => [string, string];
    exportFlow: (flowId: string) => [string, string];
};

export const QUERY_KEYS: IQueryKeys = {
    getFlows: () => ['flows'],
    getFlow: (flowId) => ['flow', flowId],
    getAgentFlows: (agentPersonId) => ['agent/flows', agentPersonId],
    exportFlow: (flowId) => ['flow/export', flowId]
};

// GET flow details
export const useFlowQuery = (flowId: string) =>
    useQuery({
        queryKey: QUERY_KEYS.getFlow(flowId),
        queryFn: async () => flowAPI.getFlow(flowId),
        gcTime: 0
    });

// CREATE empty flow
export const useCreateEmptyFlowMutation = () =>
    useMutation({
        mutationFn: async ({
            agentId,
            data
        }: {
            agentId: string;
            data: IFlowEmptyData;
        }) => flowAPI.createEmptyFlow(agentId, data)
    });

// CREATE template flow
export const useCreateTemplateFlowMutation = () =>
    useMutation({
        mutationFn: async ({
            agentId,
            data
        }: {
            agentId: string;
            data: IFlowTemplateData;
        }) => flowAPI.createTemplateFlow(agentId, data)
    });

// UPDATE flow
export const useUpdateFlowMutation = (
    options?: UseMutationOptions<
        unknown,
        Error,
        { flowId: string; data: IFlowData }
    >
) =>
    useMutation({
        ...options,
        mutationFn: async ({
            flowId,
            data
        }: {
            flowId: string;
            data: IFlowData;
        }) => flowAPI.updateFlow(flowId, data)
    });

// DELETE flow
export const useDeleteFlowMutation = () =>
    useMutation({
        mutationFn: async ({ flowId }: { flowId: string }) =>
            flowAPI.deleteFlow(flowId)
    });

// DUPLICATE flow
export const useDuplicateFlowMutation = () =>
    useMutation({
        mutationFn: async ({
            agentId,
            data
        }: {
            agentId: string;
            data: IFlowDuplicateData;
        }) => flowAPI.duplicateFlow(agentId, data)
    });
